import { Component, Element, Fragment, Listen, Method, Prop, State, Watch, forceUpdate, h } from '@stencil/core';
import Bind from 'bind-decorator';
import { generateCssClasses } from '../../utils/generate-css-classes';
import { isInStorybook } from '../../utils/is-in-storybook';
import { isTablet } from '../../utils/responsive';
import { toggleScrollLock } from '../../utils/scroll-lock';
import { hasSlotted } from '../../utils/slotted';
import { parseSpecialFalseProp } from '../../utils/special-boolean-prop';
import { LocaleService } from '../zds-config/locale-service';

export interface NavigationEventDetail {
	identifier: string;
}

export interface NavigationMaybeKeyboardEventDetail {
	identifier: string;
	isKeyboardActivated: boolean;
}

type NavigationMaybeKeyboardEvent = CustomEvent<NavigationMaybeKeyboardEventDetail>;

export interface NavigationAnimateEventDetail extends NavigationEventDetail {
	shouldAnimate?: boolean;
}

type NavigationAnimateEvent = CustomEvent<NavigationAnimateEventDetail>;

@Component({
	shadow: true,
	styleUrl: 'zds-navigation.scss',
	tag: 'zds-navigation',
})
export class ZdsNavigation {
	bodyElement?: HTMLDivElement;
	isInStorybook: boolean;
	navigationElement?: HTMLDivElement;
	wasLastBodyKeyboardActivated: boolean | null = null;

	@Element() hostElement: HTMLDivElement;

	@Prop() fullWidth: boolean;
	@Prop() skipToContentId: string | false = false;

	@State() activeBody: string;
	@State() activePanel: string;
	@State() enableNavigationBodyScroll: boolean;
	@State() opened: boolean;
	@State() shouldAnimate: boolean;

	@Watch('activeBody')
	async onActiveBodyChange(newValue: string, oldValue: string) {
		this.opened = !!newValue;

		// Hide old and show new body, simultaneously
		await Promise.all([
			this.toggleBody(oldValue, false, this.wasLastBodyKeyboardActivated),
			this.toggleBody(newValue, true, this.wasLastBodyKeyboardActivated),
		]);

		this.wasLastBodyKeyboardActivated = null;
	}

	@Watch('activePanel')
	async onActivePanelChange(newValue: string, oldValue: string) {
		// Make sure any previously active main links are marked as not active, necessary for links that are marked
		//	as active but do not actually have a corresponding panel to toggle
		this.hostElement
			.querySelectorAll<HTMLZdsNavigationLinkElement>(`zds-navigation-body-link[active]`)
			.forEach((element) => {
				element.active = false;
			});

		// Hide old and show new panel, simultaneously
		await Promise.all([this.togglePanel(oldValue, false), this.togglePanel(newValue, true)]);
	}

	@Watch('skipToContentId')
	onSkipToContentIdChange(newValue: ZdsNavigation['skipToContentId']) {
		this.skipToContentId = parseSpecialFalseProp(newValue);
	}

	@Method()
	async openBody(identifier: string, shouldAnimate: boolean = true) {
		this.shouldAnimate = shouldAnimate;
		this.activeBody = identifier;
	}

	@Method()
	async closeBody(shouldAnimate: boolean = true) {
		this.shouldAnimate = shouldAnimate;
		this.activeBody = null;
	}

	@Method()
	async openPanel(identifier: string) {
		this.activePanel = identifier;
	}

	@Method()
	async closePanel() {
		this.activePanel = null;
	}

	@Listen('zdsNavigationActivateBody')
	zdsNavigationActivateBodyHandler(event: NavigationMaybeKeyboardEvent) {
		this.wasLastBodyKeyboardActivated = event.detail?.isKeyboardActivated;

		// activeBody should be set to null if the same button was activated twice
		const newBody = event.detail?.identifier;
		this.activeBody = newBody === this.activeBody ? null : newBody;
	}

	@Listen('zdsNavigationActivatePanel')
	zdsNavigationActivatePanelHandler(event: NavigationMaybeKeyboardEvent) {
		// activePanel should be set to null if the same button was activated twice, but only if done using a keyboard
		const newPanel = event.detail?.identifier;
		this.activePanel = event.detail?.isKeyboardActivated && newPanel === this.activePanel ? null : newPanel;
	}

	@Listen('zdsNavigationBodyOpen')
	@Listen('zdsNavigationPanelOpen')
	navigationChildOpenHandler() {
		this.updateBodyHeights();

		toggleScrollLock(true);
	}

	@Listen('zdsNavigationBodyOpened')
	navigationBodyOpenedHandler() {
		// Only enable body navigation scroll after body opened, if there is no panel
		if (!this.activePanel) {
			this.enableNavigationBodyScroll = true;
		}
	}

	@Listen('zdsNavigationPanelOpened')
	navigationPanelOpenedHandler() {
		// Always enable navigation body scroll after panel opened
		this.enableNavigationBodyScroll = true;
	}

	@Listen('zdsNavigationBodyClose')
	navigationBodyCloseHandler(event: NavigationAnimateEvent) {
		// Should only set new body height when no other body has been opened
		if (this.activeBody) {
			return;
		}

		this.enableNavigationBodyScroll = false;
		this.updateBodyHeights(event.detail?.shouldAnimate);
		toggleScrollLock(false);
	}

	@Listen('zdsBackdropClosed')
	backdropCloseHandler() {
		this.activeBody = null;
	}

	@Listen('zdsLocaleChange', { target: 'window' })
	onZdsLocaleChange() {
		forceUpdate(this.hostElement);
	}

	componentWillLoad() {
		this.isInStorybook = isInStorybook();

		// Force initial call of special boolean watchers
		this.onSkipToContentIdChange(this.skipToContentId);

		// Warn when no skip-to-content target ID set
		if (!this.skipToContentId) {
			console.warn(
				'zds-navigation: no `skip-to-content-id` set, so the skip to content button will not be shown!',
			);
		}
	}

	componentDidLoad() {
		window.addEventListener('resize', this.handleWindowResize);
	}

	disconnectedCallback() {
		window.removeEventListener('resize', this.handleWindowResize);
	}

	@Bind
	handleWindowResize() {
		// if there is an active body, Check if it's display is changed to none, if it does close the navigation
		if (this.activeBody) {
			const toggledBodyElement = Array.from(
				this.hostElement.querySelectorAll<HTMLZdsNavigationBodyElement>('zds-navigation-body[slot="link"]'),
			).find((navigationBodyElement) => navigationBodyElement.identifier === this.activeBody);

			if (toggledBodyElement) {
				const style = window.getComputedStyle(toggledBodyElement);
				const display = style.getPropertyValue('display');
				if (display === 'none') {
					this.closeBody();
					return;
				}
			}
		}

		this.updateBodyHeights(false);
	}

	@Bind
	handleFocusOut(event: FocusEvent) {
		// If using tab navigation to leave the navigation, close it
		if (
			event.relatedTarget &&
			!this.hostElement.contains(event.relatedTarget as Node) &&
			!this.navigationElement.contains(event.relatedTarget as Node)
		) {
			this.closeBody();
		}
	}

	@Bind
	onSkipButtonClick() {
		this.closeBody();
	}

	render() {
		const navigationClasses = generateCssClasses('zds-navigation', {
			storybook: this.isInStorybook,
		});

		return (
			<Fragment>
				<zds-backdrop show={this.opened}></zds-backdrop>
				<div
					class={navigationClasses}
					onFocusout={this.handleFocusOut}
					ref={(element) => (this.navigationElement = element)}
				>
					<div class="zds-navigation__bar">
						{this.renderMainBar()}
						{this.renderSuperBar()}
					</div>
					{this.renderBodyBackground()}
				</div>
			</Fragment>
		);
	}

	renderMainBar() {
		const mainBarCssClasses = generateCssClasses('zds-navigation__main-bar', {
			fullWidth: this.fullWidth,
		});

		return (
			<div class="zds-navigation__main-bar-wrapper">
				<div class={mainBarCssClasses}>
					{this.renderSkipButton()}
					<div class="zds-navigation__main-bar-content">
						<slot name="logo"></slot>
						<slot name="link"></slot>
					</div>
				</div>
			</div>
		);
	}

	renderBodyBackground() {
		const bodyCssClasses = generateCssClasses('zds-navigation__body-background', {
			opened: this.opened,
		});

		return <div class={bodyCssClasses} ref={(element) => (this.bodyElement = element)}></div>;
	}

	renderSkipButton() {
		if (!this.skipToContentId) return;

		return (
			<div class="zds-navigation__skip-button-wrapper">
				<zds-button onZdsClick={this.onSkipButtonClick} link={'#' + this.skipToContentId}>
					{LocaleService.t('zdsNavigation.skipToContent')}
				</zds-button>
			</div>
		);
	}

	renderSuperBar() {
		if (!hasSlotted(this.hostElement, 'super-navigation')) return null;

		const superBarWrapperContentClasses = generateCssClasses('zds-navigation__super-bar-wrapper-content', {
			fullWidth: this.fullWidth,
		});

		return (
			<div class="zds-navigation__super-bar-wrapper">
				<div class="zds-navigation__super-bar">
					<div class={superBarWrapperContentClasses}>
						<slot name="super-navigation"></slot>
					</div>
				</div>
			</div>
		);
	}

	setBodyHeights(newHeight: number) {
		this.bodyElement.style.height = newHeight + 'px';

		this.hostElement
			.querySelectorAll('zds-navigation-body[slot=link]')
			.forEach((bodyElement: HTMLZdsNavigationBodyElement) => {
				bodyElement.setBodyHeight(newHeight);
			});
	}

	async toggleBody(identifier: string, toggle: boolean, isKeyboardActivated: boolean) {
		if (!identifier) {
			return;
		}

		// Find the body element
		const toggledBodyElement = Array.from(
			this.hostElement.querySelectorAll<HTMLZdsNavigationBodyElement>('zds-navigation-body[slot="link"]'),
		).find((navigationBodyElement) => navigationBodyElement.identifier === identifier);

		// Toggle the body element
		const toggleBodyFunc = async () => {
			await toggledBodyElement.toggle(toggle, this.shouldAnimate);

			if (!toggle && !this.activeBody) {
				// Wait until animation finished, then close active panel to reset animations
				await this.closePanel();
			}
		};

		// Toggle the corresponding navigation-link element
		toggledBodyElement.querySelector<HTMLZdsNavigationLinkElement>(`[slot="navigation-link"]`).active = toggle;

		// Open the initial panel
		let togglePanelFunc: () => Promise<void> | null = null;
		if (toggle && !isKeyboardActivated && !isTablet()) {
			const initialActiveBodyLink =
				toggledBodyElement.querySelector<HTMLZdsNavigationBodyLinkElement>(
					'zds-navigation-body-link[active]',
				) ?? toggledBodyElement.querySelector('zds-navigation-panel zds-navigation-body-link');

			togglePanelFunc = async () => {
				await initialActiveBodyLink?.activate();
			};
		}

		// Fade in body and panel simultaneously
		await Promise.all([toggleBodyFunc(), togglePanelFunc?.()]);

		this.shouldAnimate = true;
	}

	async togglePanel(identifier: string, toggle: boolean) {
		if (!identifier) {
			return;
		}

		// Find the panel element
		const toggledPanelElement = Array.from(
			this.hostElement.querySelectorAll<HTMLZdsNavigationPanelElement>('zds-navigation-panel'),
		).find((navigationPanel) => navigationPanel.identifier === identifier);

		// Toggle the panel element
		await toggledPanelElement.toggle(toggle);

		// Toggle the corresponding navigation-link element
		toggledPanelElement.querySelector<HTMLZdsNavigationBodyLinkElement>(`[slot="body-link"]`).active = toggle;
	}

	@Bind
	updateBodyHeights(shouldAnimate: boolean = true) {
		if (!shouldAnimate) {
			this.bodyElement.style.transition = 'none';
		}

		// Make sure to set the height to an actual value, as setting height from 'auto' to a different value does not
		//	animate.
		if (this.bodyElement.style.height === 'auto') {
			this.bodyElement.style.height = `${this.bodyElement.scrollHeight}px`;
		}

		// use requestAnimationFrame to make sure the height was changed from auto to something else
		requestAnimationFrame(() => {
			if (!this.opened) {
				this.setBodyHeights(0);
				return;
			}

			// Body height should be page-filling
			const bottomOffset = this.hostElement.getBoundingClientRect().bottom;
			this.setBodyHeights(window.innerHeight - bottomOffset);

			this.bodyElement.style.transition = null;
		});
	}
}
